import React from 'react';

// Candidates for keyboard focus; disabled ones and tabindex="-1" are dropped below.
const FOCUSABLE =
    'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]';

const tabbable = (root?: Element | null) =>
    Array.from(root?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        el => el.tabIndex >= 0 && !el.hasAttribute('disabled'),
    );

const wrapTab = (event: KeyboardEvent) => {
    const active = document.activeElement as HTMLElement | null;
    const items = tabbable(
        active?.closest('[role="dialog"], [role="alertdialog"]'),
    );
    const index = items.indexOf(active as HTMLElement);
    // The focused title and the container are not in the list (index -1), so Shift+Tab wraps from them too.
    const atEdge = event.shiftKey ? index <= 0 : index === items.length - 1;
    if (event.key !== 'Tab' || !items.length || !atEdge) {
        return;
    }
    event.preventDefault();
    items[event.shiftKey ? items.length - 1 : 0].focus();
};

/**
 * Keeps Tab moving between a dialog's controls while it is open. Material-UI's
 * focus trap otherwise stops on the dialog container when Tab wraps past either
 * end.
 */
export const useDialogTabWrap = (open: boolean) =>
    React.useEffect(() => {
        const target = open ? document : undefined;
        target?.addEventListener('keydown', wrapTab);
        return () => target?.removeEventListener('keydown', wrapTab);
    }, [open]);

export default useDialogTabWrap;
